import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { API, formatApiError, describeError } from "@/lib/api";
import { Btn, Card, Label, Input, Select } from "@/components/shared";
import { FileBarChart, Download, Play, Save, Trash2, Bookmark, AlertCircle } from "lucide-react";

const DATE_FIELDS = [
  { key: "created_at", label: "Created date" },
  { key: "scheduled_date", label: "Scheduled date" },
  { key: "due_date", label: "Due date" },
];

const EMPTY = {
  title: "Business Report", date_from: "", date_to: "", date_field: "created_at",
  statuses: [], service_types: [], client_ids: [], contractor_ids: [], columns: [], include_archived: false, outstanding_only: false,
};

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DATE_PRESETS = [
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_quarter", label: "This Quarter" },
  { key: "this_year", label: "This Year" },
  { key: "all", label: "All Time" },
];
function presetRange(key) {
  const now = new Date();
  const y = now.getFullYear(), mo = now.getMonth();
  if (key === "this_month") return [new Date(y, mo, 1), new Date(y, mo + 1, 0)];
  if (key === "last_month") return [new Date(y, mo - 1, 1), new Date(y, mo, 0)];
  if (key === "this_quarter") { const q = Math.floor(mo / 3); return [new Date(y, q * 3, 1), new Date(y, q * 3 + 3, 0)]; }
  if (key === "this_year") return [new Date(y, 0, 1), new Date(y, 11, 31)];
  return [null, null];
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`px-2.5 py-1 font-mono text-[11px] uppercase tracking-wide border-2 transition-colors ${active ? "bg-[#FF5722] border-[#FF5722] text-black" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
      {children}
    </button>
  );
}

export default function Reports() {
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [activePreset, setActivePreset] = useState("");

  const loadPresets = useCallback(() => { api.get("/reports/presets").then((r) => setPresets(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    api.get("/reports/options").then((r) => {
      setOpts(r.data);
      setF((prev) => ({ ...prev, columns: prev.columns.length ? prev.columns : r.data.columns.map((c) => c.key) }));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    loadPresets();
  }, [loadPresets]);

  const toggle = (key, val) => setF((prev) => {
    const arr = prev[key];
    return { ...prev, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const run = async () => {
    setRunning(true);
    try { const r = await api.post("/reports/run", f); setReport(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const setDatePreset = (key) => {
    const [from, to] = presetRange(key);
    setActivePreset(key);
    setF((p) => ({ ...p, date_from: from ? fmtDate(from) : "", date_to: to ? fmtDate(to) : "" }));
  };

  const runOutstanding = async () => {
    const cols = ["work_order", "client", "service_type", "status", "client_price", "client_paid", "balance_due", "aging_days", "aging_bucket", "due_date"];
    const nf = { ...f, outstanding_only: true,
      title: (!f.title || f.title === "Business Report") ? "Outstanding — Accounts Receivable" : f.title };
    setF(nf);
    setRunning(true);
    try { const r = await api.post("/reports/run", { ...nf, columns: cols }); setReport(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await api.post(`/reports/export.${fmt}`, f, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(f.title || "report").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Download failed — " + (await describeError(e))); }
    finally { setDownloading(""); }
  };

  const savePreset = async () => {
    if (!presetName.trim()) { toast.error("Name your report setup first"); return; }
    try { await api.post("/reports/presets", { name: presetName.trim(), config: f }); setPresetName(""); loadPresets(); toast.success("Report setup saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadPreset = (p) => { setF({ ...EMPTY, ...p.config }); toast.success(`Loaded "${p.name}"`); };
  const delPreset = async (id) => { try { await api.delete(`/reports/presets/${id}`); loadPresets(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const money = (v) => { const n = Number(v || 0); return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
  const moneyKeys = report ? new Set(report.money_keys) : new Set();

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center gap-3">
        <FileBarChart className="text-[#FF5722]" size={26} />
        <div>
          <h1 className="font-head font-black uppercase text-2xl sm:text-3xl text-white tracking-tight">Reports</h1>
          <p className="text-zinc-500 text-sm">Build job, payment & profit reports. Download as Excel, CSV or PDF.</p>
        </div>
      </div>

      {/* Saved setups */}
      {presets.length > 0 && (
        <Card className="p-4 space-y-2" data-testid="report-presets">
          <div className="flex items-center gap-2 text-zinc-400 font-mono text-[11px] uppercase tracking-wide"><Bookmark size={13} className="text-[#FF5722]" /> Saved report setups</div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <div key={p.id} className="flex items-center border-2 border-zinc-800" data-testid={`preset-${p.id}`}>
                <button onClick={() => loadPreset(p)} className="px-3 py-1.5 font-mono text-[11px] text-zinc-200 hover:text-[#FF5722]">{p.name}</button>
                <button onClick={() => delPreset(p.id)} data-testid={`del-preset-${p.id}`} className="px-2 py-1.5 text-zinc-600 hover:text-red-400 border-l-2 border-zinc-800"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Builder */}
      <Card className="p-5 space-y-5" data-testid="report-builder">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="sm:col-span-2"><Label>Report title</Label><Input data-testid="report-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          <div><Label>Date field</Label><Select data-testid="report-date-field" value={f.date_field} onChange={(e) => setF({ ...f, date_field: e.target.value })}>{DATE_FIELDS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}</Select></div>
          <div className="min-w-0"><Label>Date range</Label><div className="grid grid-cols-2 gap-2"><Input type="date" data-testid="report-from" value={f.date_from} onChange={(e) => { setActivePreset(""); setF({ ...f, date_from: e.target.value }); }} className="w-full" /><Input type="date" data-testid="report-to" value={f.date_to} onChange={(e) => { setActivePreset(""); setF({ ...f, date_to: e.target.value }); }} className="w-full" /></div></div>
        </div>

        <div><div className="font-mono text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">Quick date range</div><div className="flex flex-wrap gap-1.5">{DATE_PRESETS.map((p) => <Chip key={p.key} testid={`date-preset-${p.key}`} active={activePreset === p.key} onClick={() => setDatePreset(p.key)}>{p.label}</Chip>)}</div></div>

        {opts && (
          <>
            <div><div className="font-mono text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">Status</div><div className="flex flex-wrap gap-1.5">{opts.statuses.map((s) => <Chip key={s} testid={`filter-status-${s}`} active={f.statuses.includes(s)} onClick={() => toggle("statuses", s)}>{s.replace("_", " ")}</Chip>)}</div></div>
            <div><div className="font-mono text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">Service type</div><div className="flex flex-wrap gap-1.5">{opts.service_types.map((s) => <Chip key={s} testid={`filter-service-${s}`} active={f.service_types.includes(s)} onClick={() => toggle("service_types", s)}>{s}</Chip>)}</div></div>
            <div><div className="font-mono text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">Contractor</div><div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">{opts.contractors.map((c) => <Chip key={c.id} testid={`filter-contractor-${c.id}`} active={f.contractor_ids.includes(c.id)} onClick={() => toggle("contractor_ids", c.id)}>{c.name}</Chip>)}</div></div>
            <div><div className="font-mono text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">Client</div><div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">{opts.clients.map((c) => <Chip key={c.id} testid={`filter-client-${c.id}`} active={f.client_ids.includes(c.id)} onClick={() => toggle("client_ids", c.id)}>{c.name}</Chip>)}</div></div>
            <div>
              <div className="font-mono text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">Columns to include</div>
              <div className="flex flex-wrap gap-1.5">{opts.columns.map((c) => <Chip key={c.key} testid={`col-${c.key}`} active={f.columns.includes(c.key)} onClick={() => toggle("columns", c.key)}>{c.label}</Chip>)}</div>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer"><input type="checkbox" data-testid="report-archived" checked={f.include_archived} onChange={(e) => setF({ ...f, include_archived: e.target.checked })} /> Include archived jobs</label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer"><input type="checkbox" data-testid="report-outstanding" checked={f.outstanding_only} onChange={(e) => setF({ ...f, outstanding_only: e.target.checked })} /> Only jobs with a balance owed</label>
          </>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Btn onClick={run} disabled={running} data-testid="run-report-btn"><Play size={15} /> {running ? "Running…" : "Run report"}</Btn>
          <Btn variant="secondary" onClick={runOutstanding} disabled={running} data-testid="run-outstanding-btn"><AlertCircle size={15} /> Outstanding (owed)</Btn>
          <Btn variant="secondary" onClick={() => download("xlsx")} disabled={downloading === "xlsx"} data-testid="download-xlsx-btn"><Download size={15} /> Excel</Btn>
          <Btn variant="secondary" onClick={() => download("csv")} disabled={downloading === "csv"} data-testid="download-csv-btn"><Download size={15} /> CSV</Btn>
          <Btn variant="secondary" onClick={() => download("pdf")} disabled={downloading === "pdf"} data-testid="download-pdf-btn"><Download size={15} /> PDF</Btn>
          <div className="flex items-center gap-1 ml-auto">
            <Input placeholder="Save this setup as…" data-testid="preset-name" value={presetName} onChange={(e) => setPresetName(e.target.value)} className="w-44" />
            <Btn variant="secondary" onClick={savePreset} data-testid="save-preset-btn"><Save size={15} /> Save</Btn>
          </div>
        </div>
      </Card>

      {/* Results */}
      {report && (
        <>
          <Card className="p-5 space-y-3" data-testid="report-summary">
            <div className="font-head font-bold uppercase text-white tracking-wide">Summary</div>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
              {[["Jobs", report.summary.totals.count, false], ["Client Billed", report.summary.totals.client_price, true], ["Client Paid", report.summary.totals.client_paid, true], ["Balance Due", report.summary.totals.balance_due, true], ["Contractor Pay", report.summary.totals.contractor_pay, true], ["Company Profit", report.summary.totals.profit, true]].map(([label, val, isMoney]) => (
                <div key={label} className="border-2 border-zinc-800 p-3" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-mono text-[10px] uppercase text-zinc-500">{label}</div>
                  <div className={`font-head font-black text-lg ${label === "Company Profit" ? (report.summary.totals.profit >= 0 ? "text-emerald-400" : "text-red-400") : label === "Balance Due" ? (val > 0 ? "text-amber-400" : "text-white") : "text-white"}`}>{isMoney ? money(val) : val}</div>
                </div>
              ))}
            </div>
            {report.summary.aging?.some((a) => a.count > 0) && (
              <div className="pt-2" data-testid="ar-aging">
                <div className="font-mono text-[11px] uppercase tracking-wide text-amber-400 mb-1">Accounts Receivable — Aging (unpaid)</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {report.summary.aging.map((a) => (
                    <div key={a.bucket} className="border border-zinc-800 p-2.5" data-testid={`aging-${a.bucket}`}>
                      <div className="font-mono text-[10px] uppercase text-zinc-500">{a.bucket}</div>
                      <div className="font-head font-black text-base text-white">{money(a.balance_due)}</div>
                      <div className="font-mono text-[10px] text-zinc-600">{a.count} job{a.count === 1 ? "" : "s"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4 pt-2">
              {[["By Client", report.summary.by_client], ["By Contractor", report.summary.by_contractor]].map(([title, rows]) => (
                <div key={title}>
                  <div className="font-mono text-[11px] uppercase tracking-wide text-[#FF5722] mb-1">{title}</div>
                  <div className="border border-zinc-800 divide-y divide-zinc-800 max-h-64 overflow-y-auto">
                    <div className="grid grid-cols-4 gap-2 px-3 py-1.5 font-mono text-[9px] uppercase text-zinc-600 bg-zinc-900/50"><span>Name</span><span className="text-right">Paid</span><span className="text-right">Pay</span><span className="text-right">Profit</span></div>
                    {rows.map((g) => (
                      <div key={g.name} className="grid grid-cols-4 gap-2 px-3 py-1.5 text-xs">
                        <span className="truncate text-zinc-200">{g.name} <span className="text-zinc-600">({g.count})</span></span>
                        <span className="text-right font-mono text-zinc-300">{money(g.client_paid)}</span>
                        <span className="text-right font-mono text-zinc-400">{money(g.contractor_pay)}</span>
                        <span className={`text-right font-mono ${g.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{money(g.profit)}</span>
                      </div>
                    ))}
                    {rows.length === 0 && <div className="px-3 py-3 text-center text-zinc-600 font-mono text-xs">No data</div>}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-0 overflow-hidden" data-testid="report-table">
            <div className="px-4 py-2 font-mono text-[11px] uppercase tracking-wide text-zinc-400 border-b-2 border-zinc-800">{report.rows.length} work orders</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-zinc-900/60">{report.columns.map((c) => <th key={c.key} className="text-left px-3 py-2 font-mono text-[10px] uppercase text-zinc-500 whitespace-nowrap">{c.label}</th>)}</tr></thead>
                <tbody>
                  {report.rows.map((r, i) => (
                    <tr key={i} className="border-t border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`report-row-${i}`}>
                      {report.columns.map((c) => (
                        <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${moneyKeys.has(c.key) ? "font-mono text-right " + (c.key === "profit" ? (r.profit >= 0 ? "text-emerald-400" : "text-red-400") : "text-zinc-200") : "text-zinc-300"}`}>
                          {moneyKeys.has(c.key) ? money(r[c.key]) : (r[c.key] === "" || r[c.key] == null ? "—" : r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {report.rows.length === 0 && <tr><td colSpan={report.columns.length} className="px-3 py-6 text-center text-zinc-600 font-mono text-xs">No jobs match these filters.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
